import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  Row,
  Col,
  Switch,
  Spin,
  Tooltip,
  Alert,
  Card,
  Typography,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import { getSmtpById, updateSmtpById } from "@api/email/users/smtp";
import { getActiveDomains } from "@api/email/users/domain";
import { EmailNotificationModal } from "@components";

const { Option } = Select;
const { Title, Text } = Typography;

// Provider-specific recommended configurations
const PROVIDER_CONFIGS = {
  zoho: {
    host: "smtp.zoho.com",
    port: 587,
    secure: false,
    encryption: "STARTTLS",
    info: "Zoho Mail SMTP requires STARTTLS for port 587. Secure should be set to 'No'.",
  },
  gmail: {
    host: "smtp.gmail.com",
    port: 465,
    secure: true,
    encryption: "SSL/TLS",
    info: "Gmail SMTP with SSL works best on port 465 with secure connection.",
  },
  outlook: {
    host: "smtp.office365.com",
    port: 587,
    secure: false,
    encryption: "STARTTLS",
    info: "Outlook requires STARTTLS on port 587. Secure should be set to 'No'.",
  },
  office365: {
    host: "smtp.office365.com",
    port: 587,
    secure: false,
    encryption: "STARTTLS",
    info: "Office 365 requires STARTTLS on port 587. Secure should be set to 'No'.",
  },
  // You can add more providers as needed
};

const SmtpServerUpdateForm = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [domains, setDomains] = useState([]);
  const [serverType, setServerType] = useState("mailinabox");
  const [provider, setProvider] = useState(null);
  const [originalValues, setOriginalValues] = useState({});
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [providerRecommendation, setProviderRecommendation] = useState("");
  const [settingsChanged, setSettingsChanged] = useState(false);

  // Notification modal states
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");

  useEffect(() => {
    fetchData();
  }, [id]);

  // Update provider recommendations when provider changes
  useEffect(() => {
    if (provider && PROVIDER_CONFIGS[provider]) {
      const config = PROVIDER_CONFIGS[provider];
      setProviderRecommendation(config.info);
      setShowRecommendations(true);
    } else {
      setShowRecommendations(false);
    }
  }, [provider]);

  // Modal handling functions
  const showSuccessModal = (message) => {
    setSuccessModalVisible(true);
  };

  const showErrorModal = (message) => {
    setErrorMessage(message);
    setErrorModalVisible(true);
  };

  const showWarningModal = (message) => {
    setWarningMessage(message);
    setWarningModalVisible(true);
  };

  const showInfoModal = (message) => {
    setInfoMessage(message);
    setInfoModalVisible(true);
  };

  const fetchData = async () => {
    try {
      const [smtpResponse, domainsResponse] = await Promise.all([
        getSmtpById(id),
        getActiveDomains(),
      ]);

      const smtpData = smtpResponse.data;
      setDomains(domainsResponse.data || []);
      setServerType(smtpData.serverType);

      if (smtpData.provider) {
        setProvider(smtpData.provider);
      }

      // Store original values for comparison
      setOriginalValues({
        serverType: smtpData.serverType,
        provider: smtpData.provider,
        host: smtpData.host,
        port: smtpData.port,
        secure: smtpData.secure,
        encryption: smtpData.encryption,
      });

      form.setFieldsValue({
        serverType: smtpData.serverType,
        domain: smtpData.domain?._id || smtpData.domain,
        name: smtpData.name,
        provider: smtpData.provider,
        host: smtpData.host,
        port: smtpData.port,
        secure: smtpData.secure,
        status: smtpData.status,
        encryption: smtpData.encryption,
        apiEndpoint: smtpData.apiEndpoint,
        apiKey: smtpData.apiKey,
        maxQuota: smtpData.maxQuota,
        currentLoad: smtpData.currentLoad,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      showErrorModal("Failed to fetch SMTP server details. Please try again.");
    } finally {
      setInitializing(false);
    }
  };

  const onServerTypeChange = (value) => {
    setServerType(value);

    if (value === "mailinabox") {
      // Reset only non-SMTP fields for Mail-in-a-Box
      form.resetFields(["domain", "apiEndpoint", "apiKey", "provider"]);
      // Set default values for Mail-in-a-Box
      form.setFieldsValue({
        provider: "mailinabox",
        encryption: "SSL/TLS",
        secure: true,
      });
      setProvider("mailinabox");
    } else {
      // Reset all provider-specific fields for other types
      form.resetFields([
        "domain",
        "apiEndpoint",
        "apiKey",
        "provider",
        "encryption",
        "host",
        "port",
        "secure",
      ]);
      setProvider(null);
    }

    if (value !== originalValues.serverType) {
      setSettingsChanged(true);
    } else {
      setSettingsChanged(false);
    }

    setShowRecommendations(false);
  };

  const onProviderChange = (value) => {
    setProvider(value);

    if (value !== originalValues.provider) {
      setSettingsChanged(true);
    }

    if (PROVIDER_CONFIGS[value]) {
      showInfoModal(
        `Would you like to apply recommended settings for ${value.toUpperCase()}?`
      );
    }
  };

  const applyProviderRecommendations = () => {
    if (provider && PROVIDER_CONFIGS[provider]) {
      const config = PROVIDER_CONFIGS[provider];

      form.setFieldsValue({
        host: config.host,
        port: config.port,
        secure: config.secure,
        encryption: config.encryption,
      });

      setSettingsChanged(true);
      showInfoModal(
        `Applied recommended settings for ${provider.toUpperCase()}.`
      );
    }
  };

  const resetToOriginalSettings = () => {
    if (originalValues) {
      form.setFieldsValue({
        host: originalValues.host,
        port: originalValues.port,
        secure: originalValues.secure,
        encryption: originalValues.encryption,
      });

      setSettingsChanged(false);
      showInfoModal("Reverted to original settings.");
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Add provider settings based on selected options
      const providerSettings = {
        requireTLS: values.encryption === "STARTTLS",
        minTLSVersion: "TLSv1.2",
        ciphers: "SSLv3",
      };

      const dataToSubmit = {
        ...values,
        providerSettings,
      };

      await updateSmtpById(id, dataToSubmit);
      setLoading(false);
      showSuccessModal("SMTP server updated successfully");

      // Give time for the modal to be visible before redirecting
      setTimeout(() => {
        history.push("/super-admin/email/smtp");
      }, 1500);
    } catch (error) {
      console.error("Error updating SMTP server:", error);
      setLoading(false);
      showErrorModal(
        error.response?.data?.message || "Failed to update SMTP server"
      );
    }
  };

  const generateServerName = () => {
    const prefix = "BSAD";
    let currentProvider = form.getFieldValue("provider") || "";
    currentProvider = currentProvider.toUpperCase();

    if (currentProvider) {
      form.setFieldsValue({
        name: `${prefix}-${currentProvider}`,
      });
    }
  };

  if (initializing) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          status: "active",
          secure: true,
          maxQuota: 10240,
          currentLoad: 0,
        }}
      >
        <Card style={{ marginBottom: "20px" }}>
          <Title level={4}>SMTP Server Configuration</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Server Type"
                name="serverType"
                rules={[
                  { required: true, message: "Please select server type" },
                ]}
              >
                <Select onChange={onServerTypeChange}>
                  <Option value="mailinabox">Mail-in-a-Box</Option>
                  <Option value="other">Other Provider</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    Server Name
                    {serverType === "other" && (
                      <Tooltip title="Click on 'Generate' to automatically create a server name based on the provider">
                        <InfoCircleOutlined style={{ marginLeft: 8 }} />
                      </Tooltip>
                    )}
                  </span>
                }
                name="name"
                rules={[
                  { required: true, message: "Please input server name" },
                  { min: 3, message: "Name must be at least 3 characters" },
                ]}
                extra={
                  serverType === "other" && (
                    <Button
                      type="link"
                      onClick={generateServerName}
                      style={{ padding: 0 }}
                    >
                      Generate
                    </Button>
                  )
                }
              >
                <Input placeholder="e.g., Primary Mail Server" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {serverType === "mailinabox" ? (
          // Mail-in-a-Box specific fields
          <Card
            title="Mail-in-a-Box Configuration"
            style={{ marginBottom: "20px" }}
          >
            {/* Hidden provider field for Mail-in-a-Box */}
            <Form.Item name="provider" hidden>
              <Input />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Domain"
                  name="domain"
                  rules={[
                    { required: true, message: "Please select a domain" },
                  ]}
                >
                  <Select placeholder="Select a domain">
                    {domains.map((domain) => (
                      <Option key={domain._id} value={domain._id}>
                        {domain.domainName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="API Endpoint"
                  name="apiEndpoint"
                  rules={[
                    { required: true, message: "Please input API endpoint" },
                    { type: "url", message: "Please enter a valid URL" },
                  ]}
                >
                  <Input placeholder="https://mail.example.com/admin" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="API Key"
                  name="apiKey"
                  rules={[
                    { required: true, message: "Please input API key" },
                    {
                      min: 8,
                      message: "API key must be at least 8 characters",
                    },
                  ]}
                >
                  <Input.Password placeholder="Enter API key" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="SMTP Host"
                  name="host"
                  rules={[
                    { required: true, message: "Please input SMTP host" },
                    { type: "string", message: "Please enter a valid host" },
                  ]}
                  tooltip="SMTP server hostname (e.g., mail.yourdomain.com)"
                >
                  <Input placeholder="e.g., mail.example.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="SMTP Port"
                  name="port"
                  rules={[
                    {
                      required: true,
                      message: "Please input SMTP port number",
                    },
                    {
                      type: "number",
                      message: "Please enter a valid port number",
                    },
                    {
                      validator: (_, value) =>
                        value && (value < 1 || value > 65535)
                          ? Promise.reject("Port must be between 1 and 65535")
                          : Promise.resolve(),
                    },
                  ]}
                  tooltip="Common SMTP ports: 25 (standard), 465 (SSL), 587 (TLS/STARTTLS)"
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="e.g., 587"
                    min={1}
                    max={65535}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Secure Connection"
                  name="secure"
                  valuePropName="checked"
                  tooltip="Set to 'Yes' for SSL connections (typically port 465). Set to 'No' for STARTTLS connections (typically port 587)."
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Encryption"
                  name="encryption"
                  rules={[
                    {
                      required: true,
                      message: "Please select encryption type",
                    },
                  ]}
                  tooltip="For port 587, STARTTLS is typically used. For port 465, SSL/TLS is used."
                >
                  <Select>
                    <Option value="SSL/TLS">SSL/TLS</Option>
                    <Option value="STARTTLS">STARTTLS</Option>
                    <Option value="none">None</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        ) : (
          // External provider fields
          <Card
            title="Email Provider Configuration"
            style={{ marginBottom: "20px" }}
          >
            {showRecommendations && (
              <Alert
                message="Provider-Specific Settings"
                description={
                  <>
                    <p>{providerRecommendation}</p>
                    {settingsChanged ? (
                      <Button
                        type="link"
                        onClick={resetToOriginalSettings}
                        style={{ paddingLeft: 0 }}
                      >
                        Revert to original settings
                      </Button>
                    ) : (
                      <Button
                        type="link"
                        onClick={applyProviderRecommendations}
                        style={{ paddingLeft: 0 }}
                      >
                        Apply recommended settings
                      </Button>
                    )}
                  </>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Provider"
                  name="provider"
                  rules={[
                    { required: true, message: "Please select a provider" },
                  ]}
                >
                  <Select
                    placeholder="Select email provider"
                    onChange={onProviderChange}
                  >
                    <Option value="outlook">Outlook</Option>
                    <Option value="gmail">Gmail</Option>
                    <Option value="zoho">Zoho</Option>
                    <Option value="office365">Office 365</Option>
                    <Option value="custom">Custom</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Encryption"
                  name="encryption"
                  rules={[
                    {
                      required: true,
                      message: "Please select encryption type",
                    },
                  ]}
                  tooltip="For port 587, STARTTLS is typically used. For port 465, SSL/TLS is used."
                >
                  <Select>
                    <Option value="SSL/TLS">SSL/TLS</Option>
                    <Option value="STARTTLS">STARTTLS</Option>
                    <Option value="none">None</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Host"
                  name="host"
                  rules={[
                    { required: true, message: "Please input host" },
                    { type: "string", message: "Please enter a valid host" },
                  ]}
                >
                  <Input placeholder="e.g., smtp.example.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Port"
                  name="port"
                  rules={[
                    { required: true, message: "Please input port number" },
                    {
                      type: "number",
                      message: "Please enter a valid port number",
                    },
                    {
                      validator: (_, value) =>
                        value && (value < 1 || value > 65535)
                          ? Promise.reject("Port must be between 1 and 65535")
                          : Promise.resolve(),
                    },
                  ]}
                  tooltip="Common SMTP ports: 25 (standard), 465 (SSL), 587 (TLS/STARTTLS)"
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="e.g., 587"
                    min={1}
                    max={65535}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Secure Connection"
                  name="secure"
                  valuePropName="checked"
                  tooltip="Set to 'Yes' for SSL connections (typically port 465). Set to 'No' for STARTTLS connections (typically port 587)."
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        {/* Common fields for both types */}
        <Card title="General Settings" style={{ marginBottom: "20px" }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Max Quota (MB)"
                name="maxQuota"
                rules={[
                  { required: true, message: "Please input maximum quota" },
                  {
                    type: "number",
                    min: 1,
                    message: "Quota must be at least 1 MB",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Maximum quota in MB"
                  min={1}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Current Load (MB)"
                name="currentLoad"
                rules={[
                  { required: true, message: "Please input current load" },
                  {
                    type: "number",
                    min: 0,
                    message: "Load cannot be negative",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Current load in MB"
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Row>
          <Col span={24} style={{ textAlign: "right" }}>
            <Button
              type="default"
              onClick={() => history.push("/super-admin/email/smtp")}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ minWidth: "120px" }}
            >
              Update SMTP Server
            </Button>
          </Col>
        </Row>
      </Form>

      {/* Success Notification Modal */}
      <EmailNotificationModal
        isOpen={successModalVisible}
        onClose={() => setSuccessModalVisible(false)}
        type="success"
        title="Success"
        message="SMTP server updated successfully"
        autoCloseTime={3000}
      />

      {/* Error Notification Modal */}
      <EmailNotificationModal
        isOpen={errorModalVisible}
        onClose={() => setErrorModalVisible(false)}
        type="error"
        title="Error"
        message={errorMessage}
      />

      {/* Warning Notification Modal */}
      <EmailNotificationModal
        isOpen={warningModalVisible}
        onClose={() => setWarningModalVisible(false)}
        type="warning"
        title="Warning"
        message={warningMessage}
      />

      {/* Info Notification Modal */}
      <EmailNotificationModal
        isOpen={infoModalVisible}
        onClose={() => setInfoModalVisible(false)}
        type="info"
        title="Information"
        message={infoMessage}
      />
    </>
  );
};

export default SmtpServerUpdateForm;
